import type {
  ImportInfo,
  IndexerCategory,
  IndexerRelease,
  IndexerStatus,
  StartImportRequest,
} from '@mcgames/contracts';
import { useQueries } from '@tanstack/react-query';
import { useApiMutation, useApiQuery } from '../../api/hooks.ts';
import { http } from '../../api/http.ts';

export const INDEXER_STATUS_PATH = '/api/admin/indexer/status';
const CATEGORIES_PATH = '/api/admin/indexer/categories';
const SEARCH_PATH = '/api/admin/indexer/search';
const IMPORTS_PATH = '/api/admin/imports';

/** Releases per result page. */
export const PAGE_SIZE = 50;
const IMPORT_POLL_MS = 3000;

export type IndexerSearchParams = {
  q: string;
  /** Category id; empty for the server's default categories. */
  cat: string;
  offset: number;
};

export type IndexerSearchResult = { total: number; releases: IndexerRelease[] };

export function useIndexerStatus() {
  return useApiQuery<IndexerStatus>(INDEXER_STATUS_PATH, { staleTime: 60_000 });
}

export function useIndexerCategories() {
  return useApiQuery<IndexerCategory[]>(CATEGORIES_PATH, { staleTime: 60 * 60_000 });
}

export function searchPath({ q, cat, offset }: IndexerSearchParams): string {
  const params = new URLSearchParams();
  if (q !== '') params.set('q', q);
  if (cat !== '') params.set('cat', cat);
  params.set('offset', String(offset));
  params.set('limit', String(PAGE_SIZE));
  return `${SEARCH_PATH}?${params.toString()}`;
}

export function useIndexerSearch(params: IndexerSearchParams, enabled: boolean) {
  return useApiQuery<IndexerSearchResult>(searchPath(params), { enabled });
}

export function useStartImport() {
  return useApiMutation<StartImportRequest, ImportInfo>('POST', IMPORTS_PATH);
}

export function importPath(id: string): string {
  return `${IMPORTS_PATH}/${encodeURIComponent(id)}`;
}

/** Refetch interval of an import: every 3 s for as long as it runs. */
export function importPollInterval(info: ImportInfo | null | undefined): number | false {
  return info?.status === 'running' ? IMPORT_POLL_MS : false;
}

function importQuery(id: string) {
  return {
    queryKey: [importPath(id)],
    queryFn: () => http<ImportInfo>('GET', importPath(id)),
    refetchInterval: (query: { state: { data: ImportInfo | undefined } }) =>
      importPollInterval(query.state.data),
  };
}

/** Polls an import every 3 s for as long as it runs. */
export function useImport(id: string) {
  return useApiQuery<ImportInfo>(importPath(id), {
    refetchInterval: (query) => importPollInterval(query.state.data),
  });
}

/** The ids among `ids` whose import is running; shares the cache (and polling) with `useImport`. */
export function useRunningImportIds(ids: string[]): Set<string> {
  return useQueries({
    queries: ids.map(importQuery),
    combine: (results) =>
      new Set(ids.filter((_, index) => results[index]?.data?.status === 'running')),
  });
}
